import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Navigation, CheckCircle2, Circle, Loader2, RefreshCw } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, Btn } from "../ui";
import { getPosition } from "../lib/geo";
import api from "@/lib/api";

export default function RunNav() {
  const { runId } = useParams();
  const { store, online, version, bump, sync } = useField();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    store.getRun(runId).then(setRun);
  }, [store, runId, version]);

  const reoptimise = async () => {
    if (!online) return alert("You need to be online to re-optimise the route.");
    setBusy(true);
    try {
      const pos = await getPosition();
      await api.post(`/fieldclaim/runs/${runId}/route?start_lat=${pos.latitude}&start_lng=${pos.longitude}`);
      await sync();
      bump();
    } catch (e) {
      alert("Could not re-optimise the route.");
    } finally {
      setBusy(false);
    }
  };

  const visit = async (assetId, visited) => {
    await store.visitStop(runId, assetId, visited);
    bump();
    sync();
  };

  if (!run) return <div className="text-white/50 text-sm" data-testid="field-run-loading">Loading run…</div>;

  const stops = run.stops || [];
  const done = stops.filter((s) => s.visited).length;

  return (
    <div className="space-y-5" data-testid="field-run-nav">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">Inspection run · {run.status}</Mono>
        <h1 className="font-heading text-2xl mt-1">{run.name}</h1>
        <p className="text-white/50 text-sm mt-1">{done}/{stops.length} stops visited{run.total_distance_km ? ` · ${run.total_distance_km} km` : ""}</p>
      </div>

      <Btn onClick={reoptimise} disabled={busy} variant="ghost" testid="field-run-reoptimise">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-optimise from my location
      </Btn>

      <div className="space-y-2">
        {stops.map((s, i) => (
          <div key={s.asset_id} data-testid={`field-stop-${s.asset_id}`} className={`border rounded-sm p-3.5 ${s.visited ? "border-emerald-500/30 bg-emerald-500/5" : "border-gold-500/25 bg-navy-900/50"}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => visit(s.asset_id, !s.visited)} data-testid={`field-stop-toggle-${s.asset_id}`} className="mt-0.5">
                {s.visited ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Circle className="h-5 w-5 text-white/40" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mono className="text-[0.55rem] text-gold-500/60">Stop {i + 1}</Mono>
                  {s.leg_distance_km != null && <span className="text-[0.55rem] text-white/40">{s.leg_distance_km} km</span>}
                </div>
                <div className="font-heading text-base truncate">{s.name || s.asset_id}</div>
                {s.road_name && <div className="text-white/45 text-xs">{s.road_name}</div>}
              </div>
              {s.latitude != null && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`field-stop-nav-${s.asset_id}`}
                  className="shrink-0 inline-flex items-center gap-1 text-gold-300 text-xs border border-gold-500/25 rounded-sm px-2 py-1.5"
                >
                  <Navigation className="h-3.5 w-3.5" /> Go
                </a>
              )}
            </div>
            <button onClick={() => nav(`/field/asset/${s.asset_id}`)} data-testid={`field-stop-capture-${s.asset_id}`} className="mt-2 w-full text-center text-xs text-gold-300/80 border-t border-gold-500/15 pt-2">
              Capture evidence for this asset →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
